/* eslint-disable curly */
/* eslint-disable no-irregular-whitespace */
// import React, { Suspense } from 'react';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { gsap, TweenLite, Power1 } from 'gsap';
import { Helmet } from 'react-helmet-async';
//import { useTranslation } from 'react-i18next';
import { Route, Switch, Link } from 'react-router-dom';
//import { animated, useSpring } from 'react-spring';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useDispatch } from 'react-redux';
import { setLocale } from 'store/app/actions';
import { Locale } from 'store/app/types';
import { Cookie } from 'components/Cookie';
import favicon from '../shared/assets/favicon.png';
import { ReactComponent as Automat } from './assets/automat.svg';
import { useRouter } from './hooks/useRouter';
import { Navigation } from './components/Navigation';
import { NAVIGATION_ROUTES, SWITCH_ROUTES } from './app_routes';
import css from './App.module.css';
import './App.css';

gsap.registerEffect(TweenLite);

// Does not yet work with server side rendering:
// const Page1 = React.lazy(() => import('./pages/Page-1'));
// const Page2 = React.lazy(() => import('./pages/Page-2'));

const App: React.FC<any> = () => {
    //const { t } = useTranslation();
    const dispatch = useDispatch();
    const handleLocaleChange = useCallback(() => {
        const lang = window.localStorage.getItem('lang');
        if (lang === null) return;
        dispatch(setLocale(lang as Locale));
    }, [dispatch]);
    //const props = useSpring({ opacity: 1, from: { opacity: 0 } });
    const hasWindow = typeof window !== 'undefined' ? true : false;
    const { location } = useRouter();
    const [isLoading, setIsLoading] = useState<boolean>();
    let moving = useRef(null) as unknown | HTMLDivElement | null;
    const load = useRef<any[]>([]);

    useEffect(() => {
        if (!hasWindow) return;
        load.current = nodeListToArray(document.querySelectorAll('#load'));
        handleLocaleChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasWindow]);

    /*const transitions = useTransition(location, (location) => location.pathname, {
        from: { opacity: 0, y: 1400 },
        enter: { opacity: 1, y: 0 },
        leave: { opacity: 0 },
    });*/

    const animatedCursor = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // Use mouse for larger screens only!
        TweenLite.to(moving, 0.8, {
            x: event.clientX - 4,
            y: event.clientY - 6,
            opacity: 0.5,
            ease: Power1,
        });
        const targetElement = event.target as Element;
        const routeArr: string[] = [];
        NAVIGATION_ROUTES.forEach((r: any) => {
            routeArr.push(r.route);
        });
        routeArr.push('link');
        if (
            routeArr.includes(targetElement.classList.value) ||
            routeArr.includes(targetElement.classList.value.split(' ')[1]) ||
            targetElement.classList.value === 'am_logo_link'
        ) {
            TweenLite.to(moving, 0.4, { scale: 4, ease: Power1 });
        } else if (targetElement.classList.value === 'am_mail') {
            TweenLite.to(moving, 0.4, { scale: 6, ease: Power1 });
        } else if (targetElement.classList.value === 'am_input') {
            TweenLite.to(moving, 0.4, { scale: 4, ease: Power1 });
        } else {
            TweenLite.to(moving, 0.4, { scale: 2, ease: Power1 });
        }
    };

    const loaderEnter = () => {
        setIsLoading(true);
        load.current.forEach((loadComponent) => {
            TweenLite.fromTo(
                loadComponent,
                0.7,
                { scale: 0, rotation: 0 },
                { scale: 4, rotation: 20, ease: Power1 }
            );
        });
    };

    const loaderExit = () => {
        const timer = setTimeout(() => {
            console.log('Here');
            load.current.forEach((loadComponent) => {
                TweenLite.to(loadComponent, 0.7, { scale: 0, rotation: 0, ease: Power1 });
            });
            clearTimeout(timer);
        }, 1000);
        const clear = setTimeout(() => {
            setIsLoading(false);
            clearTimeout(clear);
        }, 2000);
    };

    const nodeListToArray = (nl: NodeList): Array<any> => {
        const arr = [];
        // eslint-disable-next-line security/detect-object-injection
        for (let i = nl.length; i--; arr.unshift(nl[i]));
        return arr;
    };

    return (
        // <Suspense fallback={<div>Loading</div>}>
        <>
            <div
                className={css.wrapper}
                id={location.pathname.substr(1)}
                onMouseMoveCapture={animatedCursor}
            >
                <Helmet
                    defaultTitle="AutoMat | Sveriges Modernaste Matkedja"
                    titleTemplate="%s AutoMat"
                    link={[{ rel: 'icon', type: 'image/png', href: favicon }]}
                />
                <div
                    className="am_loading_screen"
                    style={{ display: isLoading ? 'block' : 'none' }}
                >
                    <div className="am_loading_content">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 2066.71 1249.3"
                            preserveAspectRatio="xMidYMin slice"
                            width="120%"
                        >
                            <g id="Layer_2" data-name="Layer 2">
                                <g id="Layer_1-2" data-name="Layer 1">
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M1724.51,641.89c-39.78,17.39-78.42,36.49-75.08,104.42.38,7.83-9.66,12.49-11.93,11.63-7.63-2.88-12.65-22.14-14.61-31.16a3.36,3.36,0,0,0-5-2.15l-7.1,4.34a4.88,4.88,0,0,0-1.65,6.67l44,73.65S1612.93,835,1598,854.21s-93.49,135.52,20.22,247.66S1816,1188,1826.79,1186.14s61.41-23.32,83-31.27,43.6-46.58,61-69.6,52.44-150.43-19.66-252.94-149.9-95.39-149.9-95.39,23.8-11.6,42.8-37.87,2-21.8,29.48-42.17S1764.29,624.5,1724.51,641.89Zm-30.85,123c-15.13,7.57-33.51,30.4-33.51,30.4l-19.75-31.65,11-9.73S1699.87,761.73,1693.66,764.84Z"
                                    />
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M509.61,659.84C568.47,633.49,728.3,547.21,750.2,803.73c15.13,177.19,35,240.48-23.52,315.84-49.23,63.36-189.7,36.33-214.32-94.36S386.16,918.42,322.23,889.59C144.57,809.46,388.75,713.94,509.61,659.84Z"
                                    />
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M1417.27,1129.4c-31.19,56.44-107,221.48-278.77,29.69C1019.87,1026.6,965.9,988,967.39,892.57c1.25-80.23,129.74-143.09,228-53.47s165,9.46,233.47-5.94C1619,790.36,1481.31,1013.5,1417.27,1129.4Z"
                                    />
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M1246.49,365.25s-251.22-37.15-232.64,70.19,134.18,64,218.82,66.06,200.23,78.44,241.52,57.8S1518.36,388,1246.49,365.25Z"
                                    />
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M233.62,1031.29S-17.6,994.13,1,1101.47s134.18,64,218.82,66.06S420,1246,461.32,1225.33,505.49,1054,233.62,1031.29Z"
                                    />
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M483.33,338.22s-30.64-8-21-42.82,13.24-111.5-29-144.23-96.45-54.64-142-36.58-68.5,59.24-71.49,64.87-6.46-.87-8.13.66-10.56,14.66-8.29,15.81,6.17.22,5,2.49-30.66,57.73-27.19,98.14,37,102,91.3,118.64,111.34-7.3,129.7-32.32,37.19,2.48,36.71,6.39-4.16,17.37-21.5,19.12,5.51,11.62,10.34,7.08,18.66-18.42,20.44-17.73,19.33,9,20.16,7.53,3.1-4.69,2.41-6.24-7.49-6.48-4.23-12.92,16.41-1.34,18.52-7.45-5.11-12-3.13-17.1,3.17-6.21,3.17-6.21,22.28,10.67,24.62,9.26,9.51-2.73-2.27-11.38S490,342.64,490.24,340.68s6.39-15.21-.61-14.33S483.33,338.22,483.33,338.22Z"
                                    />
                                    <path
                                        className="cls-5"
                                        id="load"
                                        d="M1797.16,507.59c-61.73,18.67-231.22,84-220.39-173.26,7.48-177.68-4.25-243,63.41-310.31,56.88-56.59,192.78-11.95,200.62,120.8s111.62,122,171.38,158.66C2178.23,405.51,1923.9,469.27,1797.16,507.59Z"
                                    />
                                    <path
                                        className="cls-2"
                                        id="load"
                                        d="M739.56,153.68C718.45,225.8,773.09,296,779.61,319.42c3.61,12.94,6.11,30.05,7.62,42.76A2.73,2.73,0,0,1,782,363.6c-11.68-26.73-34.46-77.4-41.15-81.61a59.8,59.8,0,0,0-9-4.93,2.68,2.68,0,0,0-3.18.94c-4.24,5.85-19,27.58-18.81,46.31.17,21.88,15.19,52.8,15.93,62.64s-2.33,42.27,29.79,74.51c23.23,23.31,32.32,29.32,35.57,30.86a2.75,2.75,0,0,1,1.58,2.6c-.2,4.82-1.09,16.51-5.14,18.42-5.13,2.43-36.58,13-38.57,17.09-1.54,3.15,3.86,19.47,6.39,26.75a2.72,2.72,0,0,0,4.22,1.28c12.29-9.28,58.92-45,65.6-57.66s25.46-51.47,30.46-61.82a2.7,2.7,0,0,1,3.64-1.26c6.35,3.11,24.33,9.83,42.72-2.55,22.89-15.41,29.73-41.5,54.42-55s105.75-35.43,132-105.48,4.44-137.42-52.24-175-65.09-24.5-80.89-28.91-79.24-28.32-95.91-26.83S773,39.26,739.56,153.68ZM806.85,422.3c3.37,10.59,16.19,18.57,21.31,21.13s-15.83,48.65-29.42,57.27c-1.24.78-7.86,1-3-5.08C813.9,473.32,803.1,410.55,806.85,422.3Z"
                                    />
                                </g>
                            </g>
                        </svg>
                    </div>
                </div>
                <div className="am_parent_grid_fixed_elements">
                    <header className="am_parent_grid_header">
                        <div className="am_logo">
                            <Link className="am_logo_link" to="/">
                                <Automat />
                            </Link>
                        </div>
                        <div
                            className="am_automat"
                            style={{ pointerEvents: isLoading ? 'none' : 'all' }}
                        >
                            <Navigation navLinks={NAVIGATION_ROUTES} />
                        </div>
                    </header>
                </div>
                <div className="am_scroll_content">
                    <TransitionGroup>
                        <CSSTransition
                            timeout={500}
                            classNames="fade"
                            key={location.key}
                            onEnter={loaderEnter}
                            onExiting={loaderExit}
                        >
                            <div className="am_page_transition">
                                <Switch location={location}>
                                    {SWITCH_ROUTES.map((route: any, key) => (
                                        <Route
                                            exact
                                            key={key}
                                            path={route.route}
                                            component={route.component}
                                        />
                                    ))}
                                    <Route render={() => <div>404</div>} />
                                </Switch>
                            </div>
                        </CSSTransition>
                    </TransitionGroup>
                </div>
                <div className="am_parent_cookie">
                    <Cookie />
                </div>
                <div
                    id="am_cursor"
                    ref={(el) => {
                        moving = el;
                    }}
                />
            </div>
        </>
        // </Suspense>
    );
};

export default App;
